import React from "react";
import { Car, fieldTranslationMap } from "../Types/Car";
import "./CarInformation.css";
import { FaCar } from "react-icons/fa";
import { MdInfoOutline } from "react-icons/md";
import { FaSwatchbook } from "react-icons/fa";
interface CarInformationProps {
  car: Car;
}
// Fields only relevant to electric / plug-in vehicles
const EV_ONLY_FIELDS = new Set<keyof Car>([
  "batteryCapacity", "batteryGuaranteeMonth", "batteryGuaranteeKm",
  "electricRange", "electricConsumptionUrban", "electricConsumptionRoad",
  "electricConsumptionCombined", "chargingPower", "maximumChargingPower",
  "chargingType",
]);

// Fields only relevant to internal-combustion vehicles
const ICE_ONLY_FIELDS = new Set<keyof Car>([
  "engineSize", "consumptionUrban", "consumptionRoad", "consumptionCombined",
]);

// Fields that are shown elsewhere on the page or should never appear here
const ALWAYS_HIDDEN_FIELDS = new Set<keyof Car>([
  "price", "make", "model", "modelTypeName",
  "briefDescription", "createdAt", "updatedAt","status",
]);

// Boolean fields where `false` is still meaningful information
const BOOLEAN_FIELDS = new Set<keyof Car>([
  "roadWorthy", "steeringWheelLeft", "taxFree",
]);

const classifyFuelType = (fuelType: string): "ice" | "ev" | "hybrid" => {
  const ft = fuelType.toLowerCase();
  if (ft.includes("hybridi") || ft.includes("hybrid")) return "hybrid";
  if (ft.includes("sähkö") || ft.includes("electric")) return "ev";
  return "ice";
};

const CarInformation: React.FC<CarInformationProps> = ({ car }) => {
  const powerType = classifyFuelType(car.fuelType || "");

  const showInfo = (key: keyof Car, value: any): boolean => {
    // Always-hidden fields
    if (key.toLowerCase().includes("id")) return false;
    if (ALWAYS_HIDDEN_FIELDS.has(key)) return false;
    if (typeof value === "object") return false;

    // Empty / unfilled values — booleans are kept even when false
    if (value === null || value === undefined) return false;
    if (typeof value === "string" && value.trim() === "") return false;
    if (typeof value === "number" && value === 0 && !BOOLEAN_FIELDS.has(key)) return false;

    // Drive-power relevance
    if (powerType === "ice" && EV_ONLY_FIELDS.has(key)) return false;
    if (powerType === "ev" && ICE_ONLY_FIELDS.has(key)) return false;
    // Hybrids show both sets

    return true;
  }

  const renderIcon = (icon:"description"|"accessories"|"general") => {
    switch(icon) {
      case "description":
        const Description = FaCar as any
        return <Description />;
      case "accessories":
        const Accessories = FaSwatchbook as any
        return <Accessories />;
      case "general":
        const GeneralInfo = MdInfoOutline as any
        return <GeneralInfo />;
    }
  }

  const capitalizeFirstLetter = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const formatValue = (value: unknown): string => {
    if (typeof value === "boolean") return value ? "kyllä" : "ei";
    return String(value);
  }

  let fakeIndex=0;
  return (
    <div className="car-information-container">
      { car.briefDescription && <div className="car-information-item">
        <div className="car-information-item-title">{renderIcon("description")} Kuvaus</div>
        <div className="description">{car.briefDescription}</div>
      </div>}
      <div className="division-line"></div>
      {car?.accessories?.length>0 && <div className="car-information-item">
        <div className="car-information-item-title">{renderIcon("accessories")} Varusteet</div>
        <div className="accessories-container">
          <div className="accessories-first-half">
            {car.accessories?.map((accessory, index) => {
              const numOfAccessories = car.accessories
                ? car.accessories.length
                : 0;
              if (index >= Math.ceil(numOfAccessories / 2)) return null;
              return (
                <li key={accessory.id} className="accessory-item">
                  {accessory.fi}
                </li>
              );
            })}
          </div>
          <div className="accessories-second-half">
            {car.accessories?.map((accessory, index) => {
              const numOfAccessories = car.accessories
                ? car.accessories.length
                : 0;
              if (index < Math.ceil(numOfAccessories / 2)) return null;
              return (
                <li key={accessory.id} className="accessory-item">
                  {accessory.fi}
                </li>
              );
            })}
          </div>
        </div>
      </div>}
      <div className="division-line"></div>
      <div className="car-information-item">
        <div className="car-information-item-title">{renderIcon("general")} Yleiset tiedot</div>
        <div className="info-item-container">
          <div className="info-item-half">
            {Object.keys(car).slice(0, Math.ceil(Object.keys(car).length / 2)).map((key, index) => {
              const value = car[key as keyof Car];
              if (showInfo(key as keyof Car, value)) {  
                fakeIndex++;
                return (
                  <div key={key} className={`info-item ${fakeIndex%2 === 0 ? "even" : "odd"}`}>
                    <span className="info-key">{capitalizeFirstLetter(fieldTranslationMap[key as keyof Car] || key)}:</span>{" "}
                    <span className="info-value">{formatValue(value)}</span>
                  </div>
                );
              }
              return null;
            })}
          </div>
          <div className="info-item-half">
            {Object.keys(car).slice(Math.ceil(Object.keys(car).length / 2)).map((key, index) => {
              const value = car[key as keyof Car];
              if (showInfo(key as keyof Car, value)) {
                fakeIndex++;
                return (
                  <div key={key} className={`info-item ${fakeIndex%2 === 0 ? "even" : "odd"}`}>
                    <span className="info-key">{capitalizeFirstLetter(fieldTranslationMap[key as keyof Car] || key)}:</span>{" "}
                    <span className="info-value">{formatValue(value)}</span>
                  </div>
                );
            }
              return null;
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarInformation;
